import { useEffect, useRef, useCallback } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import changelog from '../changelog.json';

/*
 * The app's own usage records, written to the `events` collection. Anonymous: a random id per
 * device, never a person. Everything the Analytics tab and the faculty portfolio view read comes
 * from here, so each record carries the dimensions both need.
 *
 *   view      an item was opened                 { itemId, title, fileType, topic, collectionId, runId, from }
 *   complete  an item counted as done            { itemId, title, collectionId, runId, fraction }
 *   bookmark  saved / unsaved                     { itemId, title, on }
 *   test      a pre- or post-test was finished   { collectionId, title, which, score, total, runId }
 *   search    a query settled                     { scope, query, results, picked }
 *   protocol  an emergency protocol was opened   { protocolId, title, category }
 *   page      a top-level screen was shown        { page, hospitalId }
 *
 * A curriculum "run" links one learner's pre-test, completions and post-test: the device mints a
 * run id the first time it opens a curriculum, and a new one after the post-test is finished.
 */

const DEVICE_KEY = 'device-id';
const RUNS_KEY = 'curriculum-runs';

const rnd = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));

const read = (key, fallback) => {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
};
const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ } };

export const deviceId = () => {
  let id = read(DEVICE_KEY, null);
  if (!id) { id = rnd(); write(DEVICE_KEY, id); }
  return id;
};

const platform = () => {
  const ua = navigator.userAgent || '';
  const os = /iPhone|iPad|iPod/.test(ua) ? 'ios' : /Android/.test(ua) ? 'android' : /Mac/.test(ua) ? 'mac' : /Windows/.test(ua) ? 'windows' : 'other';
  const installed = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  return { os, installed: Boolean(installed || navigator.standalone) };
};

// ---- curriculum runs -------------------------------------------------------------------------

/** The current run for a curriculum on this device, minting one if needed */
export const runFor = (collectionId) => {
  if (!collectionId) return null;
  const runs = read(RUNS_KEY, {});
  if (!runs[collectionId] || runs[collectionId].finished) {
    runs[collectionId] = { runId: rnd(), startedAt: Date.now() };
    write(RUNS_KEY, runs);
  }
  return runs[collectionId].runId;
};

/** After a post-test, the run is complete; the next visit starts a fresh one */
export const finishRun = (collectionId) => {
  const runs = read(RUNS_KEY, {});
  if (runs[collectionId]) { runs[collectionId].finished = true; write(RUNS_KEY, runs); }
};

// The collection a learner is currently inside, so item events can carry it
let currentCollection = null;
export const setCurrentCollection = (id) => { currentCollection = id || null; };
export const getCurrentCollection = () => currentCollection;

// ---- writing ---------------------------------------------------------------------------------

// Aggregate labels only: the role and site or division of whoever is signed in, never a name or id
const profileLabels = () => {
  try {
    const p = JSON.parse(localStorage.getItem('profile') || 'null');
    if (!p || !p.role) return { profile: false };
    return { profile: true, role: p.role, site: p.rotation || p.site || null, division: p.division || null };
  } catch { return { profile: false }; }
};

const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''));

export const logEvent = (type, data = {}) => {
  try {
    const p = platform();
    addDoc(collection(db, 'events'), {
      type,
      ts: serverTimestamp(),
      day: new Date().toISOString().slice(0, 10),
      hour: new Date().getHours(),
      deviceId: deviceId(),
      version: changelog.currentVersion,
      os: p.os,
      installed: p.installed,
      ...profileLabels(),
      ...clean(data),
    }).catch((e) => { console.warn('telemetry: event not saved', e && (e.code || e.message)); });
  } catch { /* never let telemetry break the app */ }
};

// ---- search settling -------------------------------------------------------------------------

/**
 * Log a search once it settles rather than per keystroke: after a pause in typing, when the
 * field is left, or when a result is tapped (which also records what was picked).
 *   const { markPicked } = useSearchLog('education', query, resultCount);
 */
export const useSearchLog = (scope, query, resultCount, { settleMs = 1500, minLength = 2 } = {}) => {
  const lastLogged = useRef('');
  const timer = useRef(null);
  const q = (query || '').trim();

  const flush = useCallback((picked) => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (q.length < minLength) return;
    if (!picked && lastLogged.current === q) return;
    lastLogged.current = q;
    logEvent('search', { scope, query: q.slice(0, 120), results: resultCount, picked: picked || null });
  }, [q, scope, resultCount, minLength]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.length < minLength || resultCount == null) return undefined;
    timer.current = setTimeout(() => flush(null), settleMs);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q, resultCount, minLength, settleMs, flush]);

  // Leaving the screen with a pending query counts as settled
  useEffect(() => () => { if (timer.current && q.length >= minLength && lastLogged.current !== q) flush(null); }, [q, minLength, flush]);

  return { markPicked: (picked) => flush(picked) };
};
